import * as React from 'react'
import { Button, type ButtonProps } from './button'

const DEFAULT_CONFIRM_WINDOW_MS = 5_000

export type ConfirmButtonProps = Omit<ButtonProps, 'onClick'> & {
  /** The label announced and shown after the first activation. */
  confirmLabel: React.ReactNode
  /** Runs only after a second activation inside the confirmation window. */
  onConfirm: () => void | Promise<void>
  /** Tests may shorten the window; product call sites use the five-second default. */
  confirmWindowMs?: number
}

/**
 * One inline confirmation: activate once to reveal the exact action, then activate again within five seconds.
 * Escape, blur, timeout, or disabling the control resets it. This deliberately never opens a modal.
 */
export function ConfirmButton({
  children,
  confirmLabel,
  onConfirm,
  confirmWindowMs = DEFAULT_CONFIRM_WINDOW_MS,
  variant = 'destructive',
  disabled,
  onBlur,
  onKeyDown,
  ...props
}: ConfirmButtonProps) {
  const [armed, setArmed] = React.useState(false)

  React.useEffect(() => {
    if (!armed) return
    const timer = window.setTimeout(() => setArmed(false), confirmWindowMs)
    return () => window.clearTimeout(timer)
  }, [armed, confirmWindowMs])

  React.useEffect(() => {
    if (disabled) setArmed(false)
  }, [disabled])

  return (
    <Button
      {...props}
      variant={variant}
      disabled={disabled}
      aria-pressed={props.role === 'radio' ? undefined : armed}
      data-confirm-armed={armed ? '' : undefined}
      onBlur={(event) => {
        setArmed(false)
        onBlur?.(event)
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event)
        if (event.defaultPrevented || event.key !== 'Escape') return
        event.preventDefault()
        event.stopPropagation()
        setArmed(false)
      }}
      onClick={() => {
        if (!armed) {
          setArmed(true)
          return
        }
        setArmed(false)
        void onConfirm()
      }}
    >
      <span aria-live="polite" className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
        {armed ? confirmLabel : children}
      </span>
    </Button>
  )
}
